import { Box, Button, CircularProgress, MenuItem, Select, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useAuth } from '../../../../hooks/use-auth'
import { type SystemTimeSpan } from '../../../../models/SystemTimeSpan'
import { GetSystemConfigValues, GetSystemTimeSpans, UpdateSystemConfigValues } from '../../../../services/SystemConfigurationService'
import { SecurityLevel } from '../../../../utils/genericTypes'
import { DatePickerWithLabel } from '../../../customComponents/DatePickerWithLabel'
import { Modal } from '../../../customComponents/Modal'

interface ConfigKeyMeta {
  label: string
  description: string
}

const CONFIG_KEY_META: Record<string, ConfigKeyMeta> = {
  AnnualPlanDueDate: {
    label: 'Annual Plan Due Date',
    description: 'Deadline for each RCN\'s annual plan submission (Priority One–Four, RCN Operations, Budget Overview, and Sign Contract).'
  },
  ImplementationPhaseUpdatesDueDate: {
    label: 'Implementation Phase Updates Due Date',
    description: 'Deadline shown on the dashboard for Submit Priority One–Four Updates, RCN Operations Updates, Budget Overview, and the EPLI Trainer Initial Review.'
  },
  MidYearReviewDueDate: {
    label: 'Mid-Year Progress Report Due Date',
    description: 'Due date for the Mid-Year Progress Report and the Peer-to-Peer Data Collection System task.'
  },
  FocusStudentDataBOYDueDate: {
    label: 'Beginning-of-Year Focus Student Data Due Date',
    description: 'Due date for each ISD\'s Beginning-of-Year (BOY) Focus Student Data task.'
  },
  EndOfYearDataDueDate: {
    label: 'End of Year Data Due Date',
    description: 'Due date for each ISD\'s Coaching Capacity Tool and End-of-Year Focus Student Data tasks.'
  },
  ProgressReportEOYDueDate: {
    label: 'End-of-Year Progress Report Due Date',
    description: 'Due date for each ISD\'s End-of-Year Progress Report task.'
  },
  ImplementationPlanTrackingDueDate: {
    label: 'Implementation Plan Tracking Due Date',
    description: 'Final tracking checkpoint for each ISD\'s Priority One (EPLI Trainer Final Review).'
  }
}

const ALL_KEYS = Object.keys(CONFIG_KEY_META)

function parseDateValue (value: string | undefined): Date | null {
  if (value == null || value === '') return null
  const parts = value.split('-').map(Number)
  if (parts.length !== 3 || parts.some(isNaN)) return null
  return new Date(parts[0], parts[1] - 1, parts[2])
}

function formatDateForApi (date: Date | null): string {
  if (date == null) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const DateConfigurations = (): JSX.Element => {
  const { user } = useAuth()
  const canEdit = user?.securityLevel === SecurityLevel.ProjectAdmin
  const [timeSpans, setTimeSpans] = useState<SystemTimeSpan[]>([])
  const [selectedTimeSpanId, setSelectedTimeSpanId] = useState<number | null>(null)
  const [configValues, setConfigValues] = useState<Record<string, Date | null>>({})
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  useEffect(() => {
    const fetchTimeSpans = async (): Promise<void> => {
      const { response, success } = await GetSystemTimeSpans()
      if (!success || response == null) {
        if (!success) toast.error('Failed to load school years')
        setLoadError(true)
        setLoading(false)
        return
      }

      setTimeSpans(response)

      const today = new Date()
      const current = response.find(
        ts => new Date(ts.startDate) <= today && new Date(ts.endDate) >= today
      )
      const defaultId = current?.id ?? response[0]?.id ?? null
      setSelectedTimeSpanId(defaultId)
    }

    void fetchTimeSpans()
  }, [])

  useEffect(() => {
    if (selectedTimeSpanId == null) return

    const fetchValues = async (): Promise<void> => {
      setLoading(true)
      const { response, success } = await GetSystemConfigValues(selectedTimeSpanId)
      const raw: Record<string, string> = (success && response != null) ? response : {}
      const parsed: Record<string, Date | null> = {}
      for (const key of ALL_KEYS) {
        parsed[key] = parseDateValue(raw[key])
      }
      setConfigValues(parsed)
      setLoading(false)
    }

    void fetchValues()
  }, [selectedTimeSpanId])

  const selectedTimeSpan = timeSpans.find(ts => ts.id === selectedTimeSpanId)

  const handleDateChange = (key: string, value: Date | null): void => {
    setConfigValues(prev => ({ ...prev, [key]: value }))
  }

  const hasAllDates = ALL_KEYS.every(key => configValues[key] != null)

  const handleSave = (): void => {
    setShowConfirm(true)
  }

  const handleConfirm = async (): Promise<void> => {
    if (selectedTimeSpanId == null) return

    const values: Record<string, string> = {}
    for (const key of ALL_KEYS) {
      const date = configValues[key]
      if (date != null) {
        values[key] = formatDateForApi(date)
      }
    }

    const { success } = await UpdateSystemConfigValues(selectedTimeSpanId, values)
    if (success) {
      toast.success('Date configurations saved')
    } else {
      toast.error('Failed to save date configurations')
    }
    setShowConfirm(false)
  }

  if (loadError) {
    return <Typography color='error'>Failed to load date configurations. Please try again later.</Typography>
  }

  if (timeSpans.length === 0) {
    return <CircularProgress />
  }

  return <>
    <Box sx={{ mt: '3em' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '1em', mb: '1.5em' }}>
        <Typography variant='h5' sx={{ fontSize: '1em', fontWeight: '600' }}>Date Configurations</Typography>
        <Select
          value={selectedTimeSpanId ?? ''}
          onChange={(e) => { setSelectedTimeSpanId(Number(e.target.value)) }}
          sx={{ fontSize: '.875em', height: '2em' }}
          inputProps={{ 'aria-label': 'School Year' }}
          data-testid='dateConfigYearSelect'
        >
          {timeSpans.map(ts => (
            <MenuItem key={ts.id} value={ts.id}>{ts.name}</MenuItem>
          ))}
        </Select>
      </Box>

      {loading
        ? <CircularProgress size={24} />
        : <>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5em', maxWidth: '800px' }}>
            {ALL_KEYS.map(key => {
              const meta = CONFIG_KEY_META[key]
              const isMissing = configValues[key] == null
              const labelContent = (
                <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: '.35em' }}>
                  {meta.label}
                  <Tooltip title={meta.description} placement='top' arrow>
                    <InfoOutlinedIcon
                      fontSize='small'
                      data-testid={`dateConfigInfo-${key}`}
                      sx={{ color: 'text.secondary', fontSize: '1em', cursor: 'help' }}
                    />
                  </Tooltip>
                </Box>
              )
              return (
                <Tooltip
                  key={key}
                  title={isMissing ? 'Not yet configured for this year' : ''}
                  placement='top'
                >
                  <Box sx={{ border: isMissing ? '1px solid #f0ad4e' : 'none', borderRadius: '4px', p: isMissing ? '.5em' : 0 }}>
                    <DatePickerWithLabel
                      name={key}
                      separateLabel
                      labelContent={labelContent}
                      value={configValues[key] ?? null}
                      disabled={!canEdit}
                      dataTestId={`dateConfig-${key}`}
                      onChange={(date) => { handleDateChange(key, date) }}
                    />
                  </Box>
                </Tooltip>
              )
            })}
          </Box>

          {canEdit && (
            <Box sx={{ mt: '1.5em' }}>
              <Button
                variant='contained'
                onClick={handleSave}
                disabled={!hasAllDates}
                data-testid='saveDateConfigurations'
              >
                Save Dates
              </Button>
              {!hasAllDates && (
                <Typography variant='body2' sx={{ color: 'error.main', mt: '.5em' }}>
                  All dates must be set before saving.
                </Typography>
              )}
            </Box>
          )}
        </>
      }
    </Box>

    {showConfirm && (
      <Modal
        open={true}
        title='Save Date Configurations'
        maxWidth='sm'
        cancelButtonText='Cancel'
        confirmButtonText='Save'
        onClose={() => { setShowConfirm(false) }}
        onConfirm={() => { void handleConfirm() }}
        bodyContent={
          <Box>
            <Typography variant='body1' sx={{ mb: '1em' }}>
              You are saving the following dates for <strong>{selectedTimeSpan?.name}</strong>:
            </Typography>
            {ALL_KEYS.map(key => (
              <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', mb: '.5em' }}>
                <Typography variant='body2' sx={{ fontWeight: 500 }}>{CONFIG_KEY_META[key].label}</Typography>
                <Typography variant='body2'>
                  {configValues[key] != null
                    ? configValues[key]?.toLocaleDateString()
                    : <em style={{ color: '#999' }}>Not set</em>
                  }
                </Typography>
              </Box>
            ))}
          </Box>
        }
      />
    )}
  </>
}
